import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { T } from "@/lib/theme";
import { usePlayerCtx } from "@/context/PlayerContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_BASE = `${BACKEND_URL}/api`;

const COUNTRY_FLAGS = { "Ghana": "🇬🇭", "Nigeria": "🇳🇬", "Cote d'Ivoire": "🇨🇮", "Senegal": "🇸🇳" };
const C = T.dark;

export default function Listen() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const autoplayId = searchParams.get("station");
  const [stations, setStations] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [nowPlayingMap, setNowPlayingMap] = useState({});
  const [stats, setStats] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterMsg, setNewsletterMsg] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [tasterPanel, setTasterPanel] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const player = usePlayerCtx();
  const playing = player.playingId;

  useEffect(() => {
    (async () => {
      try {
        const [s, np, st] = await Promise.all([
          axios.get(`${API_BASE}/public/stations?limit=200`),     // ALL stations now, not just streaming
          axios.get(`${API_BASE}/public/now-playing`),
          axios.get(`${API_BASE}/public/stats`),
        ]);
        setStations(s.data.stations || []);
        const m = {};
        (np.data.now_playing || []).forEach((r) => { m[r.station_id] = r; });
        setNowPlayingMap(m);
        setStats(st.data);
      } catch (e) { console.error(e); }
    })();
    // poll now-playing every 30s
    const t = setInterval(async () => {
      try {
        const np = await axios.get(`${API_BASE}/public/now-playing`);
        const m = {};
        (np.data.now_playing || []).forEach((r) => { m[r.station_id] = r; });
        setNowPlayingMap(m);
      } catch {}
    }, 30000);
    return () => clearInterval(t);
  }, []);

  // Auto-play when arriving via ?station=ID (e.g. from Landing hero top-station cards)
  useEffect(() => {
    if (!autoplayId || !stations.length || playing) return;
    const target = stations.find((s) => s.id === autoplayId);
    if (target) {
      togglePlay(target);
      // Scroll the card into view so the listener immediately sees what's playing
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="play-${target.id}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    }
  }, [autoplayId, stations.length]);

  const togglePlay = (station) => {
    if (!station.is_streaming || !station.stream_url) {
      setTasterPanel({ stationId: station.id, stationName: station.name, data: null,
        error: `${station.name} is FM-only — online stream not available. Tap Audience to see listener data.` });
      return;
    }
    player.toggle(station);
  };

  const tasterFetch = async (station) => {
    if (tasterPanel?.stationId === station.id) { setTasterPanel(null); return; }
    try {
      const r = await axios.get(`${API_BASE}/free/taster/audience/${station.id}`);
      setTasterPanel({ stationId: station.id, stationName: station.name, data: r.data });
    } catch (e) {
      if (e.response?.status === 402) { nav("/register"); }
      else { setTasterPanel({ stationId: station.id, stationName: station.name, data: null,
        error: e.response?.data?.detail || "Audience data unavailable" }); }
    }
  };

  const shareWhatsApp = (station, np) => {
    const text = np?.now_playing
      ? `🎵 ${np.now_playing} just played on ${station.name} (${station.frequency}). Listen free: ${window.location.origin}/listen`
      : `🎧 Listening to ${station.name} on WaveScorePro: ${window.location.origin}/listen`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const copyEmbed = (station) => {
    const snippet = `<iframe src="${window.location.origin}/api/embed/${station.id}" width="320" height="170" frameborder="0" scrolling="no" style="border:0;border-radius:12px;overflow:hidden"></iframe>`;
    try {
      navigator.clipboard?.writeText(snippet)?.catch(() => {});
    } catch {}
    setCopiedId(station.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const subscribeNewsletter = async (e) => {
    e?.preventDefault?.();
    if (!newsletterEmail || !/.+@.+\..+/.test(newsletterEmail)) {
      setNewsletterMsg("Enter a valid email address");
      return;
    }
    setNewsletterLoading(true);
    setNewsletterMsg("");
    try {
      const r = await axios.post(`${API_BASE}/free/newsletter/subscribe`, {
        email: newsletterEmail,
        country: filter !== "all" ? filter : "Ghana",
      });
      setNewsletterMsg(r.data?.message || "Subscribed!");
      setNewsletterEmail("");
    } catch (err) {
      setNewsletterMsg(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setNewsletterLoading(false);
    }
  };

  const filtered = stations.filter((s) => {
    if (filter !== "all" && s.country !== filter) return false;
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const byCountry = filtered.reduce((acc, s) => {
    (acc[s.country] = acc[s.country] || []).push(s); return acc;
  }, {});

  return (
    <div data-testid="listen-page" style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Inter',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&family=Inter:wght@400;500;600;700;800&display=swap');
        body { margin:0; background:${C.bg} }
        @keyframes barL { 0%,100% { height:30% } 50% { height:90% } }`}</style>

      {/* Slim hero / nav */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" data-testid="listen-logo" style={{ textDecoration: "none", color: C.text, fontWeight: 800, fontSize: 18 }}>
          WaveScore<span style={{ color: T.greenAccent }}>Pro</span>
          <span style={{ color: C.muted, fontSize: 12, fontWeight: 400, marginLeft: 10 }}>Free radio · always on</span>
        </Link>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button data-testid="listen-signin" onClick={() => nav("/login")}
            style={{ background: "transparent", color: C.text, border: `1px solid ${C.border}`, padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
            Sign in
          </button>
          <button data-testid="listen-upgrade" onClick={() => nav("/register")}
            style={{ background: T.greenAccent, color: "#fff", border: "none", padding: "8px 18px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
            Get analytics →
          </button>
        </div>
      </div>

      {/* Search + filter */}
      <div style={{ padding: "24px 28px", maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 28, fontWeight: 800, color: C.text, margin: 0 }}>
            🎧 Listen to Ghana radio — free, no signup
          </h1>
          <p style={{ color: C.muted, fontSize: 14, marginTop: 6, maxWidth: 720 }}>
            {stats ? `${stats.streaming_stations} streaming stations across ${stats.countries} countries.` : "Loading stations…"}
            {" "}Click any station to start listening in your browser. Sign up to see live connection counts today; audience demographics arrive with Phase 2.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
          <input data-testid="listen-search" placeholder="Search station…" value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ flex: "1 1 240px", padding: "10px 14px", background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, outline: "none", fontFamily: "inherit", fontSize: 13 }} />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["all", "Ghana", "Nigeria", "Cote d'Ivoire", "Senegal"].map((c) => (
              <button key={c} data-testid={`filter-${c.replace(/\s/g, "")}`} onClick={() => setFilter(c)}
                style={{ padding: "8px 14px", background: filter === c ? T.greenAccent + "22" : "transparent",
                  border: `1px solid ${filter === c ? T.greenAccent + "55" : C.border}`,
                  color: filter === c ? T.greenAccent : C.muted, borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                {c === "all" ? "🌍 All" : `${COUNTRY_FLAGS[c] || ""} ${c}`}
              </button>
            ))}
          </div>
        </div>

        {/* Country grids; newsletter CTA is injected after the first country block,
            so the audio play buttons land above the fold. */}
        {Object.entries(byCountry).map(([country, list], idx) => (
          <div key={country} style={{ marginBottom: 28 }}>
            <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10 }}>
              {COUNTRY_FLAGS[country] || ""} {country} · {list.length} stations
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 10 }}>
              {list.map((s) => {
                const np = nowPlayingMap[s.id];
                const isPlaying = playing === s.id;
                const isStreaming = s.is_streaming && s.stream_url;
                return (
                  <div key={s.id} data-testid={`listen-card-${s.id}`}
                    style={{ background: isPlaying ? T.greenAccent + "12" : C.surface,
                      border: `1px solid ${isPlaying ? T.greenAccent + "55" : C.border}`,
                      borderRadius: 12, padding: "12px 14px", transition: "all 0.2s" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <button data-testid={`play-${s.id}`} onClick={() => togglePlay(s)}
                        title={isStreaming ? "Click to play" : "FM-only station — online stream unavailable"}
                        style={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                          background: !isStreaming ? "#33415560" : (isPlaying ? T.greenAccent : C.cyan),
                          border: "none", color: !isStreaming ? C.muted : C.bg,
                          cursor: isStreaming ? "pointer" : "not-allowed", fontSize: 16, fontWeight: 700,
                          display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {!isStreaming ? "📻" : isPlaying ? "⏸" : "▶"}
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: C.text, fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {s.name}
                        </div>
                        <div style={{ color: C.muted, fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {s.city || s.region_name} · {s.genre}
                          {!isStreaming && <span style={{ marginLeft: 6, color: "#F59E0B", fontWeight: 700, fontSize: 9, letterSpacing: "0.08em" }} data-testid={`fm-only-${s.id}`}>· FM-ONLY</span>}
                        </div>
                      </div>
                      {isPlaying && (
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 18 }}>
                          {[0, 1, 2].map((i) => (
                            <span key={i} style={{ width: 3, background: T.greenAccent, borderRadius: 2,
                              animation: `barL 0.6s ease-in-out ${i * 0.15}s infinite alternate`, height: 12 }} />
                          ))}
                        </div>
                      )}
                    </div>
                    {np?.now_playing && (
                      <div style={{ marginTop: 8, padding: "6px 10px", background: C.bg, borderRadius: 6, fontSize: 11, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        ♬ {np.now_playing}
                      </div>
                    )}
                    {/* Action row */}
                    <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                      <button data-testid={`taster-${s.id}`} onClick={() => tasterFetch(s)}
                        title="One free demographic snapshot per day. Sign up for unlimited."
                        style={{ flex: 1, padding: "6px 8px", background: C.cyan + "1a", border: `1px solid ${C.cyan}55`,
                          color: C.cyan, borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                        🔍 Audience
                      </button>
                      <button data-testid={`share-${s.id}`} onClick={() => shareWhatsApp(s, np)}
                        title="Share on WhatsApp"
                        style={{ padding: "6px 10px", background: "#25D366" + "22", border: `1px solid #25D36655`,
                          color: "#25D366", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                        📲 WhatsApp
                      </button>
                      <button data-testid={`embed-${s.id}`} onClick={() => copyEmbed(s)}
                        title="Copy embed code for your website"
                        style={{ padding: "6px 10px",
                          background: copiedId === s.id ? T.greenAccent + "22" : "transparent",
                          border: `1px solid ${copiedId === s.id ? T.greenAccent + "55" : C.border}`,
                          color: copiedId === s.id ? T.greenAccent : C.muted,
                          borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                        {copiedId === s.id ? "✓ Copied" : "⌖ Embed"}
                      </button>
                      <a data-testid={`public-page-${s.id}`} href={`/stations/${s.id}`}
                        target="_blank" rel="noreferrer"
                        title="Open public station page"
                        style={{ padding: "6px 10px", background: "transparent", border: `1px solid ${C.border}`,
                          color: C.muted, borderRadius: 6, fontSize: 11, fontWeight: 600,
                          textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
                        ↗
                      </a>
                    </div>
                    {/* Contextual claim CTA — owners spot their station here and convert in one click */}
                    <Link data-testid={`claim-cta-${s.id}`} to={`/claim?station=${s.id}`}
                      title={`Run ${s.name}? Claim it to unlock the Station Dashboard.`}
                      style={{ display: "block", marginTop: 6, padding: "5px 8px",
                        background: "transparent", border: `1px dashed ${C.border}`,
                        color: C.muted, borderRadius: 6, fontSize: 10, fontWeight: 600,
                        textAlign: "center", textDecoration: "none", letterSpacing: "0.02em" }}>
                      📻 Own this station? <span style={{ color: T.greenAccent }}>Claim it →</span>
                    </Link>
                    {/* Inline taster panel — no native alert */}
                    {tasterPanel?.stationId === s.id && (
                      <div data-testid={`taster-panel-${s.id}`}
                        style={{ marginTop: 10, padding: "12px 14px",
                          background: C.bg, borderRadius: 8, border: `1px solid ${C.cyan}33`,
                          borderLeft: `3px solid ${C.cyan}` }}>
                        {tasterPanel.error ? (
                          <div style={{ color: "#F59E0B", fontSize: 12 }}>{tasterPanel.error}</div>
                        ) : tasterPanel.data ? (() => {
                          const d = tasterPanel.data;
                          const demo = d.demographics || {};
                          const malePct = Math.round((demo.gender?.male?.pct || 0) * 100);
                          const femalePct = 100 - malePct;
                          const remaining = d.taster?.calls_remaining_today ?? 0;
                          return (
                            <>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                                <span style={{ color: C.cyan, fontSize: 12, fontWeight: 700 }}>
                                  {d.estimated_total_people.toLocaleString()} estimated listeners
                                </span>
                                <span style={{ color: C.muted, fontSize: 11 }}>
                                  {d.confidence_label} · {remaining} free peek{remaining !== 1 ? "s" : ""} left today
                                </span>
                              </div>
                              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                                {[["Male", malePct, "#3B82F6"], ["Female", femalePct, "#EC4899"]].map(([lbl, pct, color]) => (
                                  <div key={lbl} style={{ flex: 1 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                                      <span style={{ color: C.muted, fontSize: 10 }}>{lbl}</span>
                                      <span style={{ color, fontSize: 10, fontWeight: 700 }}>{pct}%</span>
                                    </div>
                                    <div style={{ height: 4, background: C.border, borderRadius: 2 }}>
                                      <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 2 }} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {remaining === 0 && (
                                <Link to="/register" style={{ display: "inline-block", marginTop: 4,
                                  color: C.cyan, fontSize: 11, fontWeight: 700, textDecoration: "none" }}>
                                  Sign up for unlimited audience data →
                                </Link>
                              )}
                            </>
                          );
                        })() : <div style={{ color: C.muted, fontSize: 12 }}>Loading...</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Newsletter CTA injected AFTER the first country block, below
                the audio player buttons so the primary action stays above the fold. */}
            {idx === 0 && (
              <div data-testid="newsletter-cta" style={{
                background: `linear-gradient(135deg, ${C.cyan}15, ${T.greenAccent}10)`,
                border: `1px solid ${C.cyan}33`, borderRadius: 14, padding: "18px 22px",
                marginTop: 22, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16
              }}>
                <div style={{ flex: "1 1 320px", minWidth: 0 }}>
                  <div style={{ color: C.cyan, fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", marginBottom: 6 }}>
                    🏆 TOP OF THE CHARTS — FREE WEEKLY
                  </div>
                  <div style={{ color: C.text, fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>
                    Get the WaveScore Charts every Friday
                  </div>
                  <div style={{ color: C.muted, fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                    Hottest songs and stations across Ghana. One-click unsubscribe. Double-opt-in, GDPR-compliant.
                  </div>
                </div>
                <form onSubmit={subscribeNewsletter} style={{ flex: "1 1 320px", display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input data-testid="newsletter-email" type="email" placeholder="you@email.com"
                    value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)} required
                    style={{ flex: "1 1 200px", padding: "10px 14px", background: C.bg,
                      border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, outline: "none",
                      fontFamily: "inherit", fontSize: 13 }} />
                  <button data-testid="newsletter-submit" type="submit" disabled={newsletterLoading}
                    style={{ padding: "10px 18px", background: T.greenAccent, color: "#fff", border: "none",
                      borderRadius: 8, cursor: newsletterLoading ? "wait" : "pointer", fontSize: 13, fontWeight: 700 }}>
                    {newsletterLoading ? "..." : "Subscribe"}
                  </button>
                </form>
                {newsletterMsg && (
                  <div data-testid="newsletter-message" style={{ flexBasis: "100%", color: C.cyan, fontSize: 12, marginTop: 2 }}>
                    {newsletterMsg}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Global PlayerBar (app-shell level) renders the persistent player */}
      </div>
    </div>
  );
}
